import { domainProvider } from '@mathflat/shared/@common/domain/domainProvider'
import type { AxiosError } from 'axios'
import axios from 'axios'
import qs from 'qs'
import { TOAST_STR } from 'src/constant'
import type { ResErr } from 'src/types/common/api'

import { CommonError } from '@/dto'
import { reporter } from '@/utils/modules/errorReporting/reporter'

import { envUtils } from '../envUtils'
import { toastError } from '../toast'
import { wrapErrorHandlerWithJwt, wrapRequestInterceptorWithJwt } from './jwtInterceptors'

const BASE_URL = (() => {
  if (envUtils.isLive) {
    return `https://messaging-api.${domainProvider.TARGET_DOMAIN}`
  }
  if (envUtils.isStaging) {
    return `https://messaging-api-staging.${domainProvider.TARGET_DOMAIN}`
  }

  return `https://messaging-api-dev.${domainProvider.TARGET_DOMAIN}`
})()

const noxios = axios.create({
  baseURL: BASE_URL,
  paramsSerializer: (params) => {
    return qs.stringify(params)
  },
})

noxios.interceptors.request.use(wrapRequestInterceptorWithJwt((config) => config))

noxios.interceptors.response.use(
  (response) => response,
  wrapErrorHandlerWithJwt(
    async (error: unknown) => {
      const axiosError = error as AxiosError<ResErr>

      if (axiosError.response) {
        const {
          config,
          data: { message, code, data },
          status,
        } = axiosError.response
        const err = new CommonError(message, { type: 'API', status, code, data, config })

        if (status === 500) {
          toastError(TOAST_STR.errorMessage)
        }

        reporter.capture(err, { mechanism: 'interceptor' })
        return Promise.reject<CommonError>(err)
      }

      reporter.capture(axiosError, { mechanism: 'interceptor' })
      return Promise.reject(axiosError)
    },
    async (originalRequest, newAccessToken) => {
      originalRequest.headers.Authorization = `Bearer ${newAccessToken}`
      return noxios(originalRequest)
    },
  ),
)

export default noxios
